import { useState } from "react";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import type { BlogPost, Page } from "./types";
import { toast } from "sonner";
import { BlogPostCard } from "./BlogComponents/BlogPostCard";
import { BlogPostForm } from "./BlogComponents/BlogPostForm";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { ProFeatureWrapper } from "./ProFeatureWrapper";
interface BlogTabProps {
  posts: BlogPost[];
  onUpdate: (posts: BlogPost[]) => void;
  isPro: boolean;
  pages: Page[];
  onBlogPageSelect: (pageId: string) => void;
  selectedBlogPageId?: string;
}
export const BlogTab = ({
  posts = [],
  onUpdate,
  isPro,
  pages,
  onBlogPageSelect,
  selectedBlogPageId
}: BlogTabProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPost, setEditingPost] = useState<BlogPost | null>(null);
  const [formData, setFormData] = useState<Partial<BlogPost>>({
    title: "",
    content: "",
    featuredImage: "",
    isPublished: false,
    tags: [],
    categories: []
  });
  const handleSubmit = () => {
    if (!formData.title || !formData.content) {
      toast.error("Title and content are required");
      return;
    }
    const newPost: BlogPost = {
      id: editingPost?.id || crypto.randomUUID(),
      title: formData.title!,
      content: formData.content!,
      excerpt: formData.excerpt || "",
      featuredImage: formData.featuredImage || "",
      publishedAt: editingPost?.publishedAt || new Date().toISOString(),
      slug: formData.title!.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
      isPublished: formData.isPublished || false,
      tags: formData.tags || [],
      categories: formData.categories || []
    };
    if (editingPost) {
      onUpdate(posts.map(post => post.id === editingPost.id ? newPost : post));
    } else {
      onUpdate([...posts, newPost]);
    }
    setIsDialogOpen(false);
    setEditingPost(null);
    setFormData({});
    toast.success(editingPost ? "Post updated" : "Post created");
  };
  const handleDelete = (id: string) => {
    onUpdate(posts.filter(post => post.id !== id));
    toast.success("Post deleted");
  };
  const handleEdit = (post: BlogPost) => {
    setEditingPost(post);
    setFormData(post);
    setIsDialogOpen(true);
  };
  return <ProFeatureWrapper feature="blog functionality" isPro={isPro}>
      <div className="space-y-4">
        <div className="flex flex-col gap-4">
          <div>
            <Label>Blog oldal</Label>
            <Select value={selectedBlogPageId} onValueChange={onBlogPageSelect}>
              <SelectTrigger>
                <SelectValue placeholder="Select a page to display blog posts" />
              </SelectTrigger>
              <SelectContent>
                {pages.map(page => <SelectItem key={page.id} value={page.id}>
                    {page.title}
                  </SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground mt-1">Ezt az oldalt fogjuk a blog oldalként kezelni. Add hozzá a Blog Lista blokkot, a bejegyzések megjelenítéséhez.</p>
          </div>

          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold">Bejegyzések</h2>
            <Button onClick={() => setIsDialogOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add Post
            </Button>
          </div>
        </div>

        <div className="grid gap-4">
          {posts.map(post => <BlogPostCard key={post.id} post={post} onEdit={handleEdit} onDelete={handleDelete} />)}
        </div>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <BlogPostForm formData={formData} onFormDataChange={setFormData} onSubmit={handleSubmit} onCancel={() => setIsDialogOpen(false)} isEditing={!!editingPost} existingCategories={Array.from(new Set(posts.flatMap(post => post.categories)))} />
        </Dialog>
      </div>
    </ProFeatureWrapper>;
};